import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Check, Archive, MessageSquare, Info } from "lucide-react";

function countSlots(text) {
  const s = String(text || "");
  const found = new Set();
  const re = /\{\{(\d+)\}\}/g;
  let m;
  while ((m = re.exec(s)) !== null) found.add(m[1]);
  return found.size;
}

function Wizard({ campaign, onCancel, onDone }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(campaign?.campaign_name || "");
  const [tpl, setTpl] = useState(campaign?.template_text || "");
  const [label, setLabel] = useState(campaign?.label || "");
  const [cid, setCid] = useState(campaign?.id || null);
  const [phone, setPhone] = useState("");
  const [params, setParams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [testErr, setTestErr] = useState("");
  const slotCount = countSlots(tpl);

  const saveDraft = async () => {
    setBusy(true);
    try {
      const r = await api.post("/campaigns/register", {
        campaign_id: cid,
        campaign_name: name.trim(),
        template_text: tpl,
        label: label || undefined,
      });
      setCid(r.data.id);
      setParams(Array.from({ length: r.data.param_count || 0 }, () => "sample"));
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setTestErr("");
    try {
      await api.post("/campaigns/verify-test-send", {
        campaign_id: cid,
        phone,
        params,
      });
      toast.success("Test send delivered. Campaign is now active.");
      onDone?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test send failed.";
      setTestErr(msg + " · Check the campaign name matches exactly and the campaign is Live in AiSensy.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-950 p-6 max-h-[90vh] overflow-y-auto" data-testid="campaign-wizard">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400">// Register Campaign · Step {step}/3</div>
            <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1">
              {campaign ? "Edit campaign" : "New campaign"}
            </h2>
          </div>
          <button onClick={onCancel} className="text-zinc-500 hover:text-zinc-200 text-xl">×</button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Campaign name — copy it exactly from your AiSensy dashboard</label>
              <input
                data-testid="wizard-campaign-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. reactivation_v1"
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
              />
              <p className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                <Info className="w-3 h-3" /> Live-API campaigns list in AiSensy → Campaigns → API Campaign name column.
              </p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Friendly label</label>
              <input
                data-testid="wizard-campaign-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Auto-uses campaign name if empty"
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onCancel} className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-zinc-800 rounded-md text-zinc-400">Cancel</button>
              <button
                onClick={() => setStep(2)}
                disabled={!name.trim()}
                data-testid="wizard-next-1"
                className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-md disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Paste the full approved template text (with {"{{1}}"}, {"{{2}}"}, …)</label>
              <textarea
                data-testid="wizard-template"
                rows={6}
                value={tpl}
                onChange={(e) => setTpl(e.target.value)}
                placeholder="Hi {{1}}, we've saved {{2}} for you. Reply YES to continue."
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white font-mono"
              />
              {slotCount > 0 && (
                <div className="mt-2 text-[11px] text-emerald-300" data-testid="wizard-slot-count">
                  This template has {slotCount} variable{slotCount === 1 ? "" : "s"}.
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-zinc-800 rounded-md text-zinc-400">Back</button>
              <button
                onClick={saveDraft}
                disabled={!tpl.trim() || busy}
                data-testid="wizard-next-2"
                className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-md disabled:opacity-40"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Save draft & continue"}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-200 p-3 text-xs">
              A verification send to your own WhatsApp number is required. The campaign only becomes active after the send succeeds.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your WhatsApp number (with country code)</label>
              <input
                data-testid="wizard-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91XXXXXXXXXX"
                className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
            {params.length > 0 && (
              <div className="space-y-2">
                {params.map((p, i) => (
                  <div key={i}>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Test value for {"{{" + (i + 1) + "}}"}</label>
                    <input
                      data-testid={`wizard-param-${i + 1}`}
                      value={p}
                      onChange={(e) => {
                        const c = [...params]; c[i] = e.target.value; setParams(c);
                      }}
                      className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
                    />
                  </div>
                ))}
              </div>
            )}
            {testErr && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-rose-200 text-xs" data-testid="wizard-test-err">
                {testErr}
              </div>
            )}
            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-zinc-800 rounded-md text-zinc-400">Back</button>
              <button
                onClick={verify}
                disabled={busy || !phone.trim()}
                data-testid="wizard-verify"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-md disabled:opacity-40"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                Send test & activate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CampaignsManager() {
  const [campaigns, setCampaigns] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => setCampaigns([]));
  useEffect(() => { load(); }, []);
  const archive = async (id) => {
    if (!window.confirm("Archive this campaign?")) return;
    try {
      await api.post(`/campaigns/${id}/archive`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed.");
    }
  };
  if (campaigns === null) {
    return <div className="text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading campaigns…</div>;
  }
  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950/60 p-6" data-testid="campaigns-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400">// AiSensy Campaigns</div>
          <h2 className="font-display font-black text-2xl text-white tracking-tight mt-1">Your campaigns</h2>
          <p className="text-xs text-zinc-500 mt-1">Register a Live-API campaign from AiSensy, verify with a real test send, then use it in Send campaign.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowWizard(true); }}
          data-testid="new-campaign-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded-md"
        >
          <Plus className="w-3.5 h-3.5" /> New campaign
        </button>
      </div>
      {campaigns.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-800 rounded-lg">
          No campaigns registered yet. Hit &quot;New campaign&quot; to add one.
        </div>
      ) : (
        <div className="space-y-2" data-testid="campaigns-list">
          {campaigns.map((c) => (
            <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-lg border border-zinc-900 bg-zinc-900/30 p-3 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">{c.label}</span>
                  {c.status === "active" ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 inline-flex items-center gap-1"><Check className="w-3 h-3" /> active</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-amber-300">draft</span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Campaign: {c.campaign_name} · {c.param_count} variable{c.param_count === 1 ? "" : "s"}</div>
                <div className="text-[11px] text-zinc-600 mt-1 font-mono truncate">{c.template_text}</div>
                {c.last_successful_send_at && (
                  <div className="text-[10px] text-zinc-600 mt-1">Last successful: {new Date(c.last_successful_send_at).toLocaleString()}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => { setEditing(c); setShowWizard(true); }}
                  className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-zinc-800 text-zinc-400 rounded"
                  data-testid={`edit-campaign-${c.id}`}
                >Edit</button>
                <button
                  onClick={() => archive(c.id)}
                  className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-zinc-800 text-zinc-500 rounded inline-flex items-center gap-1"
                  data-testid={`archive-campaign-${c.id}`}
                ><Archive className="w-3 h-3" /> Archive</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showWizard && (
        <Wizard
          campaign={editing}
          onCancel={() => setShowWizard(false)}
          onDone={() => { setShowWizard(false); load(); }}
        />
      )}
    </div>
  );
}
